// Thin fetch wrapper that handles credentials and base URL.

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function handle(res) {
  if (res.status === 401) {
    // Surface for UI to redirect to login.
    const err = new Error('unauthorized');
    err.status = 401;
    throw err;
  }

  const ct = res.headers.get('Content-Type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const err = new Error(body?.error || res.statusText);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

// Long enough for a slow barn Wi-Fi, short enough that "Saving…" on one bar of
// signal ends in an answer rather than a button that never comes back.
const TIMEOUT_MS = 20000;

async function request(path, options = {}) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      signal: typeof AbortSignal?.timeout === 'function' ? AbortSignal.timeout(TIMEOUT_MS) : undefined,
      ...options,
    });
  } catch (e) {
    // fetch rejects for exactly one family of reasons: the request never got
    // an answer. Safari says "Load failed", Chrome "Failed to fetch", a timeout
    // "The operation was aborted" — none of which tells a person in an aisle
    // what to do. Name it as the one thing it is, and keep the original text
    // so the message still carries what actually happened.
    const err = new Error(e?.name === 'TimeoutError' || e?.name === 'AbortError' ? 'timed_out' : 'no_connection');
    err.status = 0;
    err.cause = e;
    err.detail = e?.message || String(e);
    throw err;
  }
  return handle(res);
}

// Multipart upload — do NOT set Content-Type; the browser adds the multipart
// boundary itself. Used for R2 file uploads (POST /files).
async function upload(path, file, fieldOrExtras = 'file', maybeExtras) {
  // Third argument stayed a field name for the existing callers; an object
  // there means "extra form fields", which is what photo metadata needs to
  // ride along on the same multipart request.
  const field = typeof fieldOrExtras === 'string' ? fieldOrExtras : 'file';
  const extras = typeof fieldOrExtras === 'object' ? fieldOrExtras : maybeExtras;

  const form = new FormData();
  form.append(field, file);
  for (const [k, v] of Object.entries(extras || {})) {
    // Skip empties so the server sees "absent", not "set to blank".
    if (v !== undefined && v !== null && v !== '') form.append(k, String(v));
  }
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  return handle(res);
}

export const api = {
  get: (path) => request(path),
  post: (path, data) => request(path, { method: 'POST', body: JSON.stringify(data) }),
  patch: (path, data) => request(path, { method: 'PATCH', body: JSON.stringify(data) }),
  // PUT where the write is idempotent — setting a day's hours twice is the
  // same day with the same hours, not two overrides.
  put: (path, data) => request(path, { method: 'PUT', body: JSON.stringify(data) }),
  del: (path) => request(path, { method: 'DELETE' }),
  upload,
};
